import { flexRender, Row, Table } from '@tanstack/react-table';
import cx from 'classnames';
import React, { Fragment } from 'react';
import Pagination from '../../Pagination';
import { InContentAd } from '../../ads/inContentAd';
import { IconButton } from '../../presentational/buttons/IconButton';
import { ComplexTooltip } from '../../presentational/tooltip';
import { Stack } from '../../stack/Stack';
import { useTableState } from '../lib';
import { DefaultDataType, IDataTableProps, IRowSelectionConfig, TColumn } from '../types';
import { isInteractiveElement } from '../utils';
import _ from 'lodash';
import { RioLoader } from '../../loader';

export function DataTable<T extends DefaultDataType = DefaultDataType>(props: IDataTableProps<T>) {
  const {
    expandableConfig,
    pagination,
    isPaginationVisible,
    tableInstance,
    isEmpty,
    columnsLength,
    expandable,
    getHeaderSorting,
    getRowKey,
    getExpandableRow,
    paginationConfig
  } = useTableState(props);

  const {
    hideHeader,
    className,
    containerClassName,
    scrollable,
    isLoading,
    emptyContent,
    getRowProps,
    muteValues,
    wrapperProps,
    data,
    columns,
    footer,
    pagination: propPagination,
    renderToolbar,
    getRowKey: _domUnsafeGetRowKey,
    getCellProps,
    rowSelection,
    expandable: _expandable,
    ...restProps
  } = props;
  void _domUnsafeGetRowKey;

  const selection = getSelectionState(rowSelection, tableInstance, getRowKey);

  const { showPaginationTop = true, showPaginationBottom = true } = paginationConfig;
  const toolbar = renderToolbar?.({
    tableInstance,
    rows: tableInstance.getPrePaginationRowModel().rows,
    isLoading,
    isEmpty
  });
  const showTopPagination = isPaginationVisible && showPaginationTop;

  return (
    <div {...wrapperProps}>
      <Pagination
        removeVerticalMargins
        top={showTopPagination ? true : toolbar ? <></> : false}
        bottom={isPaginationVisible && showPaginationBottom}
        navButtonsProps={pagination}
        upperChildren={toolbar ? <div className="slds-m-bottom--small">{toolbar}</div> : undefined}
      >
        <div className={cx('rio-data-table__container', getScrollClassName(scrollable), containerClassName)}>
          <table {...restProps} className={cx(className)}>
            {!hideHeader && (
              <thead>
                {tableInstance.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {selection && (
                      <th style={{ width: selection.columnWidth }} className="slds-text-align--center">
                        {selection.showSelectAll && (
                          <SelectAllCheckbox
                            checked={selection.allSelected}
                            indeterminate={selection.someSelected && !selection.allSelected}
                            disabled={selection.selectableCount === 0}
                            onChange={selection.toggleAll}
                          />
                        )}
                      </th>
                    )}
                    {expandableConfig.isEnabled && expandableConfig.showToggleColumn && (
                      <th style={{ width: 24, padding: 0 }} className="slds-text-align--center"></th>
                    )}

                    {headerGroup.headers.map((column) => {
                      const isSorted = column.column.getIsSorted();
                      const canSort = column.column.getCanSort();
                      const original = column.getContext().column.columnDef as TColumn<T>;

                      const { clickHandler: handleClickSorting, sortDirections } = getHeaderSorting(column);

                      return (
                        <th
                          key={column.id}
                          colSpan={column.colSpan}
                          className={cx(
                            {
                              'cell-action': canSort && !isEmpty,
                              sorted: isSorted,
                              unsorted: !isSorted,
                              'sorted-asc': isSorted === 'asc',
                              'sorted-desc': isSorted === 'desc',
                              'slds-text-align--center': original.align === 'center',
                              'slds-text-align--right': original.align === 'right',
                              'slds-text-align--left': original.align === 'left'
                            },
                            original.headerClassName
                          )}
                          onClick={handleClickSorting}
                          style={{ width: original.width }}
                        >
                          {canSort && !isEmpty ? (
                            <div className="slds-grid slds-grid--align-spread">
                              <div style={{ flex: '1 1 auto' }}>
                                {original.tooltip ? (
                                  <ComplexTooltip className="span" tooltip={original.tooltip} placement="top">
                                    {flexRender(column.column.columnDef.header, column.getContext())}
                                  </ComplexTooltip>
                                ) : (
                                  flexRender(column.column.columnDef.header, column.getContext())
                                )}
                              </div>

                              <Stack spacingInRems={1} alignItems="center" className="slds-m-left--xx-small">
                                <div
                                  className={cx('sort-icons', {
                                    asc: isSorted === 'asc',
                                    desc: isSorted === 'desc',
                                    both: sortDirections === 'both',
                                    one: sortDirections !== 'both'
                                  })}
                                >
                                  {(sortDirections === 'both' || sortDirections.includes('asc')) && (
                                    <i className="fa-solid fa-sort-up sort-up"></i>
                                  )}
                                  {(sortDirections === 'both' || sortDirections.includes('desc')) && (
                                    <i className="fa-solid fa-sort-down sort-down"></i>
                                  )}
                                </div>
                              </Stack>
                            </div>
                          ) : (
                            <>
                              {original.tooltip ? (
                                <ComplexTooltip className="span" tooltip={original.tooltip} placement="top">
                                  {flexRender(column.column.columnDef.header, column.getContext())}
                                </ComplexTooltip>
                              ) : (
                                flexRender(column.column.columnDef.header, column.getContext())
                              )}
                            </>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
            )}
            <tbody>
              {isLoading ? (
                <tr className="no-action">
                  <td colSpan={columnsLength}>
                    <div className="empty-content-wrapper">
                      <RioLoader size="lg" />
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {!isEmpty ? (
                    tableInstance.getRowModel().rows.map((row, rowIndex) => {
                      const { isRowExpandable, handleClickExpand, isExpanded } = getExpandableRow(row);

                      const rowId = getRowKey ? getRowKey(row.original as T) : row.id;

                      const rows = tableInstance.getRowModel().rows;
                      const showInterstitialBeforeRow =
                        props.showInterstitialEvery &&
                        rows.length > props.showInterstitialEvery &&
                        rowIndex > 0 &&
                        rowIndex % props.showInterstitialEvery === 0;
                      const interstitialOffset = props.showInterstitialEvery
                        ? Math.floor(rowIndex / props.showInterstitialEvery) - 1
                        : 0;

                      const rowProps = getRowProps ? getRowProps(row.original as T) : {};

                      return (
                        <Fragment key={rowId}>
                          {showInterstitialBeforeRow ? (
                            <tr>
                              <td colSpan={columnsLength} className="slds-text-align--center slds-p-around--x-small">
                                {props.renderInterstitialContent ? (
                                  props.renderInterstitialContent(interstitialOffset, rowIndex)
                                ) : (
                                  <InContentAd />
                                )}
                              </td>
                            </tr>
                          ) : null}
                          <tr
                            {...rowProps}
                            onClick={
                              isRowExpandable
                                ? (e: React.MouseEvent) => {
                                    if (!isInteractiveElement(e.target)) {
                                      handleClickExpand();
                                    }
                                  }
                                : rowProps.onClick
                            }
                            style={{
                              ...(isRowExpandable ? { cursor: 'pointer' } : {}),
                              ...rowProps.style
                            }}
                          >
                            {selection && (
                              <td className="slds-text-align--center" style={{ width: selection.columnWidth }}>
                                {(() => {
                                  const { disabled, checked } = selection.getRowState(row);
                                  return (
                                    <input
                                      type={selection.type === 'radio' ? 'radio' : 'checkbox'}
                                      checked={checked}
                                      disabled={disabled}
                                      onChange={() => selection.toggleRow(row)}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                                      aria-label={`select-row-${getRowKey ? getRowKey(row.original as any) : row.id}`}
                                    />
                                  );
                                })()}
                              </td>
                            )}
                            {expandable && expandableConfig.showToggleColumn && (
                              <td className="slds-text-align--center" style={{ padding: 0 }}>
                                {isRowExpandable && (
                                  <IconButton
                                    variant="text"
                                    size="small"
                                    icon={expandableConfig.icon!}
                                    style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                    onClick={handleClickExpand}
                                  />
                                )}
                              </td>
                            )}
                            {row.getAllCells().map((cell, cellIndex) => {
                              const column = cell.column.columnDef as TColumn<T>;
                              const isSorted = cell.column.getIsSorted();

                              const contextRow = cell.getContext().row;

                              let value: any;

                              if (column.accessor) {
                                value =
                                  typeof column.accessor === 'function'
                                    ? column.accessor(contextRow.original)
                                    : _.get(contextRow.original, column.accessor);
                              } else {
                                value = cell.getValue();
                              }

                              const cellProps = getCellProps ? getCellProps(column, contextRow.original as T) : {};

                              return (
                                <td
                                  key={cell.id}
                                  {...cellProps}
                                  className={cx(
                                    {
                                      'slds-text-align--center': column.align === 'center',
                                      'slds-text-align--right': column.align === 'right',
                                      'slds-text-align--left': column.align === 'left',
                                      'text-muted': muteValues?.includes(cell.renderValue()),
                                      sorted: isSorted
                                    },
                                    column.cellClassName,
                                    cellProps.className
                                  )}
                                  style={{ width: column.width, ...cellProps.style }}
                                >
                                  {column.cellRender
                                    ? flexRender(
                                        column.cellRender.bind(this, contextRow.original, {
                                          dataTableRow: contextRow,
                                          dataTableRowIndex: rowIndex,
                                          dataTableCellIndex: cellIndex,
                                          columns: tableInstance.getAllColumns(),
                                          value,
                                          isRowExpandable,
                                          isExpanded,
                                          toggleExpand: handleClickExpand,
                                          setExpansionType: expandableConfig?.onSetExpansionType
                                            ? (type: string) => expandableConfig.onSetExpansionType!(rowId, type)
                                            : undefined
                                        }),
                                        {}
                                      )
                                    : (cell.renderValue() as React.ReactNode)}
                                </td>
                              );
                            })}
                          </tr>

                          {isExpanded && (
                            <tr className="no-action">
                              <td colSpan={columnsLength}>{expandableConfig?.getChildren(row.original as T)}</td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })
                  ) : (
                    <>
                      {emptyContent ? (
                        <tr className="no-action rio-full-height">
                          <td colSpan={columnsLength} className="slds-text-align--center">
                            <div className="empty-content-wrapper">{emptyContent}</div>
                          </td>
                        </tr>
                      ) : null}
                    </>
                  )}
                </>
              )}
            </tbody>
            {footer && (
              <tfoot>
                <tr>
                  <td colSpan={columnsLength}>{footer}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Pagination>
    </div>
  );
}

function getScrollClassName(scrollable: IDataTableProps['scrollable']) {
  if (!scrollable) {
    return undefined;
  }

  if (scrollable === 'x') {
    return 'rio-data-table__container--scroll-x';
  }

  if (scrollable === 'y') {
    return 'rio-data-table__container--scroll-y';
  }

  return 'rio-data-table__container--scroll';
}

type TSelectionState<T extends DefaultDataType> = {
  type: 'checkbox' | 'radio';
  columnWidth: string | number;
  showSelectAll: boolean;
  allSelected: boolean;
  someSelected: boolean;
  selectableCount: number;
  toggleAll: () => void;
  toggleRow: (row: Row<T>) => void;
  getRowState: (row: Row<T>) => { checked: boolean; disabled: boolean };
};

// Resolves the injected selection column: the controlled selection is a set of keys
// (via getRowKey); select-all and change events are scoped to the current page's rows.
function getSelectionState<T extends DefaultDataType>(
  config: IRowSelectionConfig<T> | undefined,
  tableInstance: Table<T>,
  getRowKey?: (row: T) => string
): TSelectionState<T> | null {
  if (!config) {
    return null;
  }

  const type = config.type ?? 'checkbox';
  const selectedKeys = new Set(config.selectedRowKeys);
  const rows = tableInstance.getRowModel().rows;
  const keyOf = (row: Row<T>) => (getRowKey ? getRowKey(row.original as T) : row.id);
  const isDisabled = (row: Row<T>) => !!config.getCheckboxProps?.(row.original as T)?.disabled;

  const selectableRows = rows.filter((row) => !isDisabled(row));
  const allSelected = selectableRows.length > 0 && selectableRows.every((row) => selectedKeys.has(keyOf(row)));
  const someSelected = selectableRows.some((row) => selectedKeys.has(keyOf(row)));

  const emit = (nextKeys: Set<string>) => {
    const keys = [...nextKeys];
    const keyToRow = new Map(rows.map((row) => [keyOf(row), row.original as T]));
    config.onChange(
      keys,
      keys.map((key) => keyToRow.get(key)).filter((row): row is T => Boolean(row))
    );
  };

  const toggleRow = (row: Row<T>) => {
    const key = keyOf(row);
    if (type === 'radio') {
      emit(new Set([key]));
      return;
    }
    const next = new Set(selectedKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    emit(next);
  };

  const toggleAll = () => {
    const next = new Set(selectedKeys);
    selectableRows.forEach((row) => (allSelected ? next.delete(keyOf(row)) : next.add(keyOf(row))));
    emit(next);
  };

  return {
    type,
    columnWidth: config.columnWidth ?? 0,
    showSelectAll: type === 'checkbox' && !config.hideSelectAll,
    allSelected,
    someSelected,
    selectableCount: selectableRows.length,
    toggleAll,
    toggleRow,
    getRowState: (row) => ({ checked: selectedKeys.has(keyOf(row)), disabled: isDisabled(row) })
  };
}

// `indeterminate` is a DOM-only property, so it has to be set imperatively.
function SelectAllCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: () => void;
}) {
  const ref = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      aria-label="select-all-rows"
    />
  );
}
